import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import { CdkDrag, type CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import type { TableActionByValue, TableActionCase, TableMatrixCapabilities, TableMatrixColumn } from '../../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { normalizeTableCellButtons, normalizeTableCellConfig, type TableCellButton, type TableCellConfig } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { TableCellConfigEditorComponent } from '../shared/table-cell-config-editor.component';
import { CellButtonsComponent } from '../../design-builder/panels/inspector/cell-buttons.component';
import { CellButtonFeatureComponent } from '../../design-builder/panels/inspector/cell-button-feature.component';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile, DrillDownDimension, DrillDownMeasure, TableTypeOption, TmColumnDef } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import { distinctDropdownOptions } from '../dropdown-step/dropdown-options.utils';
import { map, type Observable } from 'rxjs';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { yearFilterBaseFrom, yearFilterCountFrom, yearFilterFieldFrom } from '../drill-down-step/drill-format.utils';
import type { DrillDownStepComponent } from '../drill-down-step/drill-down-step.component';
import { extractTableMatrixRows } from './table-matrix-rows.utils';
import { sanitizeApiCallConfig } from '../../dvm-layout-builder/dvm-input-api-call.utils';

/** An action column's On click choices, when the builder config gives none. */
const TM_COLUMN_ACTION_KINDS: LabelValueOption[] = [
  { label: 'Several buttons (each its own feature)', value: 'buttons' },
  { label: 'Call API', value: 'api' },
  { label: 'Row details popup', value: 'rowPopup' },
  { label: 'API popup (show response)', value: 'apiPopup' },
  { label: 'Popup component (blocks)', value: 'popupComponent' },
  { label: 'Emit event only', value: 'emit' },
];

/**
 * Table & Matrix build form (TABLE_MATRIX): the table type picker (Normal /
 * Editable / Drill-Down, plus legacy shapes restored from saved tiles), the
 * per-column cards (editability, actions, filters), the two-view toggle,
 * cell navigation to another dashboard, capabilities and compute payloads.
 * The Drill-Down type composes the drill-down step (`this.drill`).
 */
@Component({
  selector: 'app-table-matrix-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective, TableCellConfigEditorComponent, CellButtonsComponent, CellButtonFeatureComponent,
    CdkDropList, CdkDrag, CdkDragHandle],
  templateUrl: './table-matrix-step.component.html',
  styles: [
    ':host { display: contents; }',
    // Column cards: drag by the handle (or use the arrows) to reorder the table's columns.
    '.tm-drag-handle { cursor: grab; color: #94a3b8; display: inline-flex; align-items: center; }',
    '.tm-drag-handle:active { cursor: grabbing; }',
    '.tm-move-button { width: 24px; height: 24px; padding: 0; line-height: 24px; }',
    '.tm-move-button mat-icon { font-size: 18px; width: 18px; height: 18px; }',
    '.tm-column-card.cdk-drag-placeholder { opacity: .35; }',
    '.tm-column-card.cdk-drag-preview { background: #fff; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, .18); }',
    '.tm-column-list.cdk-drop-list-dragging .tm-column-card:not(.cdk-drag-placeholder) { transition: transform 200ms ease; }',
  ],
})
export class TableMatrixStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['TABLE_MATRIX'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  /** Per column: a stable loader of the column's distinct dataset values
   *  (Cell formatting → Value colours → "From dataset"). */
  private readonly tmValueLoaders = new Map<string, () => Observable<string[]>>();
  private readonly tmValuesMemo = new WeakMap<TmColumnDef, { rows: unknown; field: string; items: string[] }>();
  /** Distinct values of a column — from the generated table's own rows (the
   *  tile being edited), else the dataset's cached sample rows; undefined when
   *  neither is at hand (the editor then offers "From dataset"). */
  tmColumnValues(column: TmColumnDef): string[] | undefined {
    if (!column.field || column.isAction) return undefined;
    const tile = this.host.editingTileId ? this.host.findTileAnywhere(this.host.editingTileId) : undefined;
    const data = tile?.layoutType === 'TABLE_MATRIX' ? tile.tableMatrixData : undefined;
    let rows = (Array.isArray(data?.['staticData']) ? data!['staticData'] : Array.isArray(data?.['rows']) ? data!['rows'] : undefined) as Array<Record<string, unknown>> | undefined;
    if (!rows?.length) rows = this.host.cachedRows(this.tmDatasetFor(column));
    if (!rows?.length) return undefined;
    const memo = this.tmValuesMemo.get(column);
    if (memo && memo.rows === rows && memo.field === column.field) return memo.items;
    const items = distinctDropdownOptions(rows, column.field);
    this.tmValuesMemo.set(column, { rows, field: column.field, items });
    return items;
  }
  tmValuesLoader(column: TmColumnDef): (() => Observable<string[]>) | null {
    const dataset = this.tmDatasetFor(column);
    if (!column.field || column.isAction || !dataset) return null;
    const key = `${dataset}\u0000${column.field}`;
    let loader = this.tmValueLoaders.get(key);
    if (!loader) {
      const url = resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.filterApi, '/getData');
      const field = column.field;
      loader = () => this.datasetService.getDistinctFieldValues(url, dataset, field).pipe(map((rows) => distinctDropdownOptions(rows, field)));
      this.tmValueLoaders.set(key, loader);
    }
    return loader;
  }
  private pendingPrune = '';

  /** The drill-down step (always instantiated by the shell) — owns the Drill-Down type's state. */
  private get drill(): DrillDownStepComponent {
    return this.host.drillDownStep;
  }

  tmType = 'normal';
  tmMigrationNotice = '';
  tmColumns: TmColumnDef[] = [];
  get tmColumnFields(): string[] {
    return this.tmColumns.map((column) => column.field).filter(Boolean);
  }
  tmToggleEnabled = false;
  tmToggleLabel1 = '';
  tmToggleLabel2 = '';
  tmToggleDataset = '';
  tmToggleColumns: TmColumnDef[] = [];
  private isTmToggleColumn(column: TmColumnDef): boolean {
    return this.tmToggleColumns.some((item) => item.id === column.id);
  }
  private touchTmColumns(column: TmColumnDef): void {
    if (this.isTmToggleColumn(column)) this.tmToggleColumns = [...this.tmToggleColumns];
    else this.tmColumns = [...this.tmColumns];
  }
  private tmDatasetFor(column: TmColumnDef): string {
    return column.dataset || (this.isTmToggleColumn(column) ? this.tmToggleDatasetEffective : this.host.drillDataset);
  }
  tmGroupBy = '';
  tmAggregation = 'none';
  tmRowField = '';           // Matrix row dimension
  tmColumnField = '';        // Matrix column dimension
  tmValueField = '';         // Matrix/Pivot measure
  tmValueFields: string[] = [];  // Pivot MULTI-measure (each fans out per column value)
  tmRowFields: string[] = [];    // Pivot row fields
  tmColumnFields2: string[] = []; // Pivot column fields
  tmGroupByFields: string[] = []; // Grouped: ordered group levels
  tmGroupsExpanded = true;
  tmTreeIdField = '';        // Tree Grid: row id column
  tmTreeParentField = '';    // Tree Grid: parent id column
  tmRoleField = '';          // Financial: row role column
  tmIndentField = '';        // Financial: indent column
  tableMatrixWidgetData: Record<string, unknown> | null = null;

  tmNavEnabled = false;
  tmNavFilterKey = '';
  tmNavColumns: string[] = [];
  tmNavTargetDashboard = '';
  tmNavTargetComponents: string[] = [];
  tmNavButtonLabel = 'Navigate';
  tmNavAccordionOpen = false;

  private readonly tableTypesFallback: TableTypeOption[] = [
    { label: 'Normal Table', value: 'normal', group: 'Standard', normal: true },
    { label: 'Editable Table', value: 'editable', group: 'Interactive', editable: true },
    { label: 'Drill-Down Table', value: 'drilldown', group: 'Interactive', drilldown: true },
    { label: 'Nested Table', value: 'nested', group: 'Interactive', nested: true },
  ];
  private static readonly LEGACY_TABLE_TYPES: Record<string, { label: string; flag: string }> = {
    normal: { label: 'Normal Table', flag: 'normal' },
    editable: { label: 'Editable Table', flag: 'editable' },
    drilldown: { label: 'Drill-Down Table', flag: 'drilldown' },
    matrix: { label: 'Matrix Table', flag: 'matrix' },
    pivot: { label: 'Pivot Table', flag: 'pivot' },
    grouped: { label: 'Grouped Table', flag: 'grouped' },
    treegrid: { label: 'Tree Grid', flag: 'treegrid' },
    financial: { label: 'Financial Table', flag: 'financial' },
  };
  private legacyTableTypesCache: { base: TableTypeOption[]; type: string; items: TableTypeOption[] } | null = null;

  get tableTypes(): TableTypeOption[] {
    const configured = ((this.host.layout.options as Record<string, unknown> | undefined)?.['tableTypes'] as TableTypeOption[] | undefined)
      ?? this.tableTypesFallback;
    const type = this.tmType;
    if (!type || configured.some((entry) => entry.value === type)) return configured;
    const cache = this.legacyTableTypesCache;
    if (cache && cache.base === configured && cache.type === type) return cache.items;
    const legacy = TableMatrixStepComponent.LEGACY_TABLE_TYPES[type];
    const entry: TableTypeOption = {
      label: `${legacy?.label ?? type} (legacy)`,
      value: type,
      group: 'Legacy',
      hint: 'This table type is no longer offered for new tables (restored from a saved tile). It stays editable here; the Drill-Down Table now covers pivot layouts.',
      [legacy?.flag ?? type]: true,
    };
    const items = [...configured, entry];
    this.legacyTableTypesCache = { base: configured, type, items };
    return items;
  }
  get tmTypeEntry(): TableTypeOption | undefined {
    return this.tableTypes.find((entry) => entry.value === this.tmType);
  }
  tmFlagsFor(type: string): Record<string, boolean> {
    const entry = this.tableTypes.find((e) => e.value === type);
    const flags: Record<string, boolean> = {};
    if (entry) {
      Object.keys(entry).forEach((key) => {
        if (entry[key] === true) flags[key] = true;
      });
    }
    if (Object.keys(flags).length === 0) {
      const legacy = TableMatrixStepComponent.LEGACY_TABLE_TYPES[type];
      if (legacy) flags[legacy.flag] = true;
      else if (type === 'editable') flags['editable'] = true;
      else if (type === 'drilldown') flags['drilldown'] = true;
      else flags['normal'] = true;
    }
    return flags;
  }
  private legacyPivotToDrilldown(
    payload: Record<string, unknown>,
    caps: Record<string, unknown>,
  ): { dimensions: DrillDownDimension[]; columnDimensions: DrillDownDimension[]; measures: DrillDownMeasure[] } | null {
    const type = String(payload['subType'] ?? caps['tableType'] ?? '');
    if (type !== 'pivot') return null;
    const configured = ((this.host.layout.options as Record<string, unknown> | undefined)?.['tableTypes'] as TableTypeOption[] | undefined)
      ?? this.tableTypesFallback;
    if (configured.some((entry) => entry.value === 'pivot')) return null;
    if (!configured.some((entry) => entry.value === 'drilldown' || entry['drilldown'] === true)) return null;

    const list = (value: unknown, single: unknown): string[] => {
      const many = Array.isArray(value) ? (value as unknown[]).map(String).filter(Boolean) : [];
      if (many.length) return many;
      const one = String(single ?? '');
      return one ? [one] : [];
    };
    const rowFields = list(caps['rowFields'], caps['rowField']);
    const columnFields = list(caps['columnFields'], caps['columnField']);
    const valueFields = list(caps['valueFields'], caps['valueField']);
    if (!rowFields.length && !valueFields.length) return null;

    const displayNames = new Map<string, string>();
    (Array.isArray(payload['columns']) ? payload['columns'] as unknown[] : []).forEach((column) => {
      if (column && typeof column === 'object') {
        const field = String((column as Record<string, unknown>)['field'] ?? '');
        const displayName = String((column as Record<string, unknown>)['displayName'] ?? '');
        if (field && displayName) displayNames.set(field, displayName);
      }
    });
    const labelFor = (field: string): string => displayNames.get(field) ?? field;
    const toDimension = (field: string): DrillDownDimension => ({
      id: this.drill.nextDimensionId(), field, label: labelFor(field), valueOrder: '', editable: false, dataset: '',
    });
    const rawAggregation = String(caps['aggregation'] ?? 'sum').toLowerCase();
    const aggregation = rawAggregation === 'none' ? 'SUM' : rawAggregation.toUpperCase();
    const rollUpStrategy = ['SUM', 'AVG', 'MIN', 'MAX'].includes(aggregation) ? aggregation : 'SUM';
    const total = caps['showGrandTotal'] === true;
    return {
      dimensions: rowFields.map(toDimension),
      columnDimensions: columnFields.map(toDimension),
      measures: valueFields.map((field) => ({
        id: this.drill.nextMeasureId(), field, label: labelFor(field),
        aggregation, rollUpStrategy, total, editable: false, dataset: '',
      })),
    };
  }
  get isTmNormal(): boolean {
    return !!this.tmFlagsFor(this.tmType)['normal'];
  }
  get isTmEditable(): boolean {
    return !!this.tmFlagsFor(this.tmType)['editable'];
  }
  get isTmDrilldown(): boolean {
    return !!this.tmFlagsFor(this.tmType)['drilldown'];
  }
  get isTmMatrix(): boolean { return !!this.tmFlagsFor(this.tmType)['matrix']; }
  get isTmPivot(): boolean { return !!this.tmFlagsFor(this.tmType)['pivot']; }
  get isTmGrouped(): boolean { return !!this.tmFlagsFor(this.tmType)['grouped']; }
  get isTmTreegrid(): boolean { return !!this.tmFlagsFor(this.tmType)['treegrid']; }
  get isTmFinancial(): boolean { return !!this.tmFlagsFor(this.tmType)['financial']; }
  get isTmFlatShape(): boolean {
    return this.active && !this.isTmDrilldown;
  }
  get tmTypeHint(): string {
    return String(this.tmTypeEntry?.['hint'] ?? '');
  }
  get tmAggregations(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmAggregations'] as LabelValueOption[] | undefined) ?? [
      { label: 'None (raw rows)', value: 'none' },
      { label: 'Sum', value: 'sum' },
      { label: 'Average', value: 'avg' },
      { label: 'Count', value: 'count' },
    ];
  }
  private readonly tmColumnControlsFallback: LabelValueOption[] = [
    { label: 'Text box', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Dropdown', value: 'dropdown' },
    { label: 'Checkbox', value: 'checkbox' },
    { label: 'Date', value: 'date' },
    { label: 'Text area', value: 'textarea' },
  ];
  get tmColumnControls(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmColumnControls'] as LabelValueOption[] | undefined)
      ?? this.tmColumnControlsFallback;
  }
  get tmColumnAlignments(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmColumnAlignments'] as LabelValueOption[] | undefined) ?? [
      { label: 'Left', value: 'start' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'end' },
    ];
  }
  get tmColumnActionKinds(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmColumnActionKinds'] as LabelValueOption[] | undefined)
      ?? TM_COLUMN_ACTION_KINDS;
  }
  get tmColumnActionMethods(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmColumnActionMethods'] as LabelValueOption[] | undefined) ?? [
      { label: 'POST', value: 'POST' },
      { label: 'GET', value: 'GET' },
      { label: 'PUT', value: 'PUT' },
      { label: 'DELETE', value: 'DELETE' },
    ];
  }
  get tmActionSortDirections(): LabelValueOption[] {
    return [
      { label: 'Newest first (desc)', value: 'desc' },
      { label: 'Oldest first (asc)', value: 'asc' },
    ];
  }

  tmActionNeedsUrl(column: TmColumnDef): boolean {
    const kind = column.actionKind || 'emit';
    return kind === 'api' || kind === 'apiPopup';
  }

  get tmPopupComponentOptions(): LabelValueOption[] {
    return this.host.dashboardTiles
      .filter((tile) => tile.layoutType === 'POPUP' && !!tile.componentId)
      .map((tile) => ({
        label: tile.title?.trim() || String(tile.componentId),
        value: String(tile.componentId),
      }));
  }

  private tmPopupDataFor(componentId: string): Record<string, unknown> | null {
    const id = String(componentId ?? '').trim();
    if (!id) return null;
    const tile = this.host.dashboardTiles.find(
      (item) => item.layoutType === 'POPUP' && String(item.componentId ?? '') === id,
    );
    const data = tile?.popupControl?.widgetData;
    return data && typeof data === 'object' ? { ...(data as Record<string, unknown>) } : null;
  }

  tmPopupComponentMissing(column: TmColumnDef): boolean {
    const id = String(column.popupComponentId ?? '').trim();
    if (!id || column.actionKind !== 'popupComponent') return false;
    return !this.tmPopupComponentOptions.some((option) => option.value === id);
  }

  isTmMaterialIcon(icon?: string): boolean {
    return !!icon && /^[a-z][a-z0-9_]*$/i.test(icon.trim());
  }
  get tmInitialFilterOperators(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmInitialFilterOperators'] as LabelValueOption[] | undefined) ?? [
      { label: 'Equals', value: 'eq' },
      { label: 'Not equals', value: 'neq' },
      { label: 'Contains', value: 'contains' },
      { label: 'In (comma-separated list)', value: 'in' },
    ];
  }

  get tmNavTargetComponentOptions(): LabelValueOption[] {
    return this.host.navTargetComponentOptions(this.tmNavTargetDashboard);
  }

  onTmNavTargetDashboardChange(): void {
    this.tmNavTargetComponents = [];
    this.onTableMatrixTypeChange();
  }
  toggleTmNavAccordion(): void { this.tmNavAccordionOpen = !this.tmNavAccordionOpen; }

  onTmColumnFieldDrop(event: DragEvent, toggle = false): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    if (!toggle) this.host.useDroppedSingleDataset(mapping.dataset);
    const fallback = toggle ? this.tmToggleDatasetEffective : this.host.drillDataset;
    const dataset = mapping.dataset || fallback;
    const list = toggle ? this.tmToggleColumns : this.tmColumns;
    const duplicate = list.some(
      (column) => column.field === mapping.field && (column.dataset || fallback) === dataset,
    );
    if (!duplicate) {
      const row: TmColumnDef = {
        id: this.nextTmColumnId(),
        dataset,
        field: mapping.field,
        displayName: this.defaultColumnDisplayName(mapping.field),
        align: '',
        filterField: '',
        filterOperator: 'eq',
        filterValue: '',
      };
      if (toggle) this.tmToggleColumns = [...this.tmToggleColumns, row];
      else this.tmColumns = [...this.tmColumns, row];
      if (dataset) this.host.loadColumnsForDataset(dataset);
    }
    this.onTableMatrixTypeChange();
  }

  onTmGroupByFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.tmGroupBy = mapping.field;
    this.onTableMatrixTypeChange();
  }

  onTableMatrixTypeChange(): void {
    // A nested table has its own form (and backend shape); this one steps aside.
    if (this.tmType === 'nested') {
      this.tmType = 'normal';
      this.host.switchTableType('nested');
      return;
    }
        this.host.generateError = '';
    this.refreshEditingTableMatrixTile();
  }

  private refreshEditingTableMatrixTile(): void {
    if (!this.host.editingTileId) return;
    const tile = this.host.findTileAnywhere(this.host.editingTileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const capabilities = this.buildTableMatrixCapabilities(tile.format);
    tile.tableMatrixData = { ...tile.tableMatrixData, capabilities };
    this.host.updateTileInPlace({ ...tile });
  }

  addTmColumn(toggle = false): void {
    const row: TmColumnDef = {
      id: this.nextTmColumnId(),
      dataset: toggle ? this.tmToggleDatasetEffective : this.host.drillDataset,
      field: '',
      displayName: '',
      align: '',
      filterField: '',
      filterOperator: 'eq',
      filterValue: '',
    };
    if (toggle) this.tmToggleColumns = [...this.tmToggleColumns, row];
    else this.tmColumns = [...this.tmColumns, row];
  }

  /** Dragged to a new place in the column list - the table shows columns in this order. */
  onTmColumnDrop(event: CdkDragDrop<TmColumnDef[]>, toggle: boolean): void {
    if (event.previousIndex === event.currentIndex) return;
    this.reorderTmColumns(toggle, event.previousIndex, event.currentIndex);
  }

  /** The ↑ / ↓ buttons: one place up (-1) or down (+1). */
  moveTmColumn(column: TmColumnDef, delta: -1 | 1): void {
    const toggle = this.isTmToggleColumn(column);
    const from = (toggle ? this.tmToggleColumns : this.tmColumns).indexOf(column);
    this.reorderTmColumns(toggle, from, from + delta);
  }

  private reorderTmColumns(toggle: boolean, from: number, to: number): void {
    const list = [...(toggle ? this.tmToggleColumns : this.tmColumns)];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return;
    moveItemInArray(list, from, to);
    if (toggle) this.tmToggleColumns = list;
    else this.tmColumns = list;
    this.onTableMatrixTypeChange();
  }

  removeTmColumn(column: TmColumnDef): void {
    if (this.isTmToggleColumn(column)) this.tmToggleColumns = this.tmToggleColumns.filter((item) => item.id !== column.id);
    else this.tmColumns = this.tmColumns.filter((item) => item.id !== column.id);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDatasetChange(column: TmColumnDef, dataset: string | null): void {
    column.dataset = dataset || '';
    column.field = '';
    this.touchTmColumns(column);
    if (column.dataset) this.host.loadColumnsForDataset(column.dataset);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFieldChange(column: TmColumnDef, field: string | null): void {
    const previousDefault = this.defaultColumnDisplayName(column.field);
    column.field = field || '';
    if (!column.displayName || column.displayName === previousDefault) {
      column.displayName = this.defaultColumnDisplayName(column.field);
    }
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDisplayNameChange(column: TmColumnDef, displayName: string): void {
    column.displayName = displayName;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  readonly tmColumnNumberTypes = [
    { value: 'number', label: 'Number (1,234)' },
    { value: 'currency', label: 'Currency ($1,234)' },
    { value: 'percent', label: 'Percent (12.3%)' },
  ];

  onTmColumnFormulaChange(column: TmColumnDef, formula: string): void {
    column.formula = formula;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnTypeChange(column: TmColumnDef, type: string | null): void {
    column.type = this.asTmColumnType(type);
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDecimalsChange(column: TmColumnDef, decimals: unknown): void {
    column.decimals = this.asTmColumnDecimals(decimals);
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnEditableChange(column: TmColumnDef, editable: boolean): void {
    column.editable = editable;
    if (editable && !column.control) column.control = 'text';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnControlChange(column: TmColumnDef, control: string | null): void {
    column.control = control || 'text';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnHiddenChange(column: TmColumnDef, hidden: boolean): void {
    column.hidden = hidden;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnSaveAllTriggerChange(column: TmColumnDef, saveAllTrigger: boolean): void {
    column.saveAllTrigger = saveAllTrigger;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnMandatoryChange(column: TmColumnDef, mandatory: boolean): void {
    column.mandatory = mandatory;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnIsActionChange(column: TmColumnDef, isAction: boolean): void {
    column.isAction = isAction;
    if (isAction) {
      column.actionKind = column.actionKind || 'rowPopup';
      column.buttonIcon = column.buttonIcon || 'visibility';
      column.actionMethod = column.actionMethod || 'POST';
      column.editable = undefined;
      column.control = '';
      column.hidden = false;
      column.mandatory = false;
      column.saveAllTrigger = false;
    }
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** The action column's buttons: any = a buttons column; none = back to one button. */
  setTmColumnButtons(column: TmColumnDef, buttons: TableCellButton[]): void {
    column.cellButtons = buttons;
    if (buttons.length) column.actionKind = 'buttons';
    else if (column.actionKind === 'buttons') column.actionKind = 'emit';
    this.onTmColumnActionChange();
  }

  /** "Hide buttons" input: comma-separated labels → the column's hiddenButtons. */
  setTmColumnHiddenButtons(column: TmColumnDef, text: string): void {
    const labels = String(text ?? '').split(',').map((label) => label.trim()).filter(Boolean);
    column.hiddenButtons = labels.length ? labels : undefined;
    this.onTmColumnActionChange();
  }

  onTmColumnActionChange(): void {
    this.tmColumns = [...this.tmColumns];
    this.tmToggleColumns = [...this.tmToggleColumns];
    this.onTableMatrixTypeChange();
  }

  tmActionBodyError(column: TmColumnDef): string {
    const raw = String(column.actionBody ?? '').trim();
    if (!raw) return '';   // blank = send the whole row; nothing to validate
    const stubbed = raw
      .replace(/"\{[^{}"]+\}"/g, '"x"')   // quoted token → a string
      .replace(/\{[^{}"]+\}/g, 'null');   // bare token   → a JSON value
    try {
      const parsed = JSON.parse(stubbed);
      if (parsed === null || typeof parsed !== 'object') {
        return 'Body should be a JSON object or array.';
      }
      return '';
    } catch (error) {
      return `Invalid JSON — ${(error as Error).message}`;
    }
  }

  tmActionTokens(): string[] {
    return this.tmColumns
      .filter((column) => !column.isAction && column.field)
      .map((column) => `{${column.field}}`);
  }

  appendTmActionToken(column: TmColumnDef, token: string): void {
    const field = token.replace(/[{}]/g, '');
    const current = String(column.actionBody ?? '').trim();
    column.actionBody = current
      ? `${current.replace(/\}\s*$/, '').replace(/,\s*$/, '')},\n  "${field}": ${token}\n}`
      : `{\n  "${field}": ${token}\n}`;
    this.onTmColumnActionChange();
  }

  // ── Buttons by row value (column.actionByValue) ─────────────────────────
  // A case is either a single button (its own label/style/tone/action) or a `buttons` list;
  // the editor shows both as a list of buttons, and a case with none shows a dash.

  readonly tmActionCaseStyles: LabelValueOption[] = [
    { label: 'Button', value: 'button' },
    { label: 'Link', value: 'link' },
  ];

  readonly tmActionCaseTones: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Green', value: 'green' },
    { label: 'Red', value: 'red' },
    { label: 'Blue', value: 'blue' },
    { label: 'Amber', value: 'amber' },
    { label: 'Gray', value: 'gray' },
  ];

  /** What a per-row button does; "Same as the column" keeps the column's own On click, and
   *  "Button feature" runs anything a Button widget can (popup screen, navigate, API, approval ...). */
  // The SAME array while the column's kinds are unchanged: a fresh one each change detection
  // made ng-select redraw its options on mousedown, so a click on one never landed.
  get tmActionCaseKinds(): LabelValueOption[] {
    const source = this.tmColumnActionKinds;
    if (this.tmActionCaseKindsMemo?.source !== source) {
      const own = new Set(['api', 'rowPopup', 'apiPopup', 'emit']);
      this.tmActionCaseKindsMemo = { source, list: [
        { label: 'Same as the column', value: '' },
        { label: 'Button feature (popup, navigate, API, approval...)', value: 'button' },
        ...source.filter((kind) => own.has(String(kind.value))),
      ] };
    }
    return this.tmActionCaseKindsMemo.list;
  }
  private tmActionCaseKindsMemo?: { source: LabelValueOption[]; list: LabelValueOption[] };

  /** The table's columns, any of which can decide a row's buttons. */
  tmActionValueFieldOptions(): LabelValueOption[] {
    return this.tmColumns
      .filter((column) => !column.isAction && column.field)
      .map((column) => ({ label: column.displayName ? `${column.displayName} (${column.field})` : column.field, value: column.field }));
  }

  /** Turns per-row buttons on (rows with no matching value show nothing) or off. */
  setTmActionByValueEnabled(column: TmColumnDef, enabled: boolean): void {
    if (enabled) {
      const status = this.tmColumns.find((other) => !other.isAction && /status/i.test(other.field));
      column.actionByValue = { field: status?.field ?? '', cases: {}, default: { style: 'none' } };
    } else {
      delete column.actionByValue;
    }
    this.onTmColumnActionChange();
  }

  setTmActionByValueField(column: TmColumnDef, field: string | null): void {
    if (!column.actionByValue) return;
    column.actionByValue.field = String(field ?? '').trim();
    this.onTmColumnActionChange();
  }

  tmActionCases(column: TmColumnDef): Array<{ value: string; actionCase: TableActionCase }> {
    return Object.entries(column.actionByValue?.cases ?? {}).map(([value, actionCase]) => ({ value, actionCase }));
  }

  trackByTmActionCase(index: number): number {
    return index;
  }

  addTmActionCase(column: TmColumnDef): void {
    const byValue = column.actionByValue;
    if (!byValue) return;
    byValue.cases = byValue.cases ?? {};
    let value = 'NEW_VALUE';
    for (let n = 2; Object.prototype.hasOwnProperty.call(byValue.cases, value); n++) value = `NEW_VALUE_${n}`;
    const button: TableActionCase = { label: 'New button', style: 'button' };
    byValue.cases[value] = { buttons: [button] };
    this.tmOpenActionButtons.add(button);
    this.onTmColumnActionChange();
  }

  /** Renames a case's value in place, keeping the cases' order; a blank or taken value is ignored. */
  renameTmActionCase(column: TmColumnDef, oldValue: string, newValue: string): void {
    const byValue = column.actionByValue;
    const value = String(newValue ?? '').trim();
    if (!byValue?.cases || !value || value === oldValue || Object.prototype.hasOwnProperty.call(byValue.cases, value)) {
      this.onTmColumnActionChange();
      return;
    }
    byValue.cases = Object.fromEntries(
      Object.entries(byValue.cases).map(([key, actionCase]) => [key === oldValue ? value : key, actionCase]),
    );
    this.onTmColumnActionChange();
  }

  removeTmActionCase(column: TmColumnDef, value: string): void {
    if (!column.actionByValue?.cases) return;
    delete column.actionByValue.cases[value];
    this.onTmColumnActionChange();
  }

  /** Buttons for rows whose value matches no case: none until set; removing it gives them the column's button. */
  addTmActionDefault(column: TmColumnDef): void {
    if (!column.actionByValue) return;
    column.actionByValue.default = { style: 'none' };
    this.onTmColumnActionChange();
  }

  removeTmActionDefault(column: TmColumnDef): void {
    if (!column.actionByValue) return;
    delete column.actionByValue.default;
    this.onTmColumnActionChange();
  }

  /** A case's buttons: its `buttons`, else the case itself as one button, else none (a dash). */
  tmCaseButtons(actionCase: TableActionCase): TableActionCase[] {
    if (Array.isArray(actionCase.buttons)) return actionCase.buttons;
    return actionCase.style === 'none' ? [] : [actionCase];
  }

  addTmCaseButton(actionCase: TableActionCase): void {
    if (!Array.isArray(actionCase.buttons)) {
      // A single-button case becomes a list, its one button moving into it.
      const single = actionCase.style === 'none' ? null : this.takeTmSingleButton(actionCase);
      delete actionCase.style;
      actionCase.buttons = single ? [single] : [];
    }
    const button: TableActionCase = { label: 'New button', style: 'button' };
    actionCase.buttons.push(button);
    this.tmOpenActionButtons.add(button);
    this.onTmColumnActionChange();
  }

  removeTmCaseButton(actionCase: TableActionCase, index: number): void {
    if (Array.isArray(actionCase.buttons)) {
      actionCase.buttons.splice(index, 1);
      if (!actionCase.buttons.length) {
        delete actionCase.buttons;
        actionCase.style = 'none';
      }
    } else {
      this.takeTmSingleButton(actionCase);
      actionCase.style = 'none';
    }
    this.onTmColumnActionChange();
  }

  /** Expanded per-row buttons in the editor (by object, so it survives reordering). */
  private readonly tmOpenActionButtons = new Set<TableActionCase>();

  isTmCaseButtonOpen(button: TableActionCase): boolean {
    return this.tmOpenActionButtons.has(button);
  }

  toggleTmCaseButton(button: TableActionCase): void {
    if (!this.tmOpenActionButtons.delete(button)) this.tmOpenActionButtons.add(button);
  }

  /** Sets one setting of a per-row button; blank removes it, so the button falls back to the column's. */
  setTmCaseButtonValue(button: TableActionCase, key: keyof TableActionCase, value: unknown): void {
    const record = button as Record<string, unknown>;
    if (value === '' || value === null || value === undefined || value === false) delete record[key];
    else record[key] = value;
    // A Button feature needs its settings and an id (its busy / result state on the dashboard).
    if (key === 'actionKind' && value === 'button') {
      button.config = button.config ?? { action: 'none' };
      button.id = button.id || `abv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    }
    this.onTmColumnActionChange();
  }

  /** The embedded Button settings changed some feature keys of a per-row button. */
  patchTmCaseButtonConfig(button: TableActionCase, patch: Record<string, unknown>): void {
    button.config = { ...(button.config ?? {}), ...patch };
    this.onTmColumnActionChange();
  }

  /** Whether a per-row button calls an API, by its own On click or else the column's. */
  tmCaseButtonCallsApi(button: TableActionCase, column: TmColumnDef): boolean {
    const kind = button.actionKind || column.actionKind;
    return kind === 'api' || kind === 'apiPopup';
  }

  /** The JSON check of a per-row button's request body (same rules as the column's). */
  tmCaseButtonBodyError(button: TableActionCase): string {
    return this.tmActionBodyError({ actionBody: button.actionBody } as TmColumnDef);
  }

  /** Removes a single-button case's own button settings and returns them as a button. */
  private takeTmSingleButton(actionCase: TableActionCase): TableActionCase {
    const keys: Array<keyof TableActionCase> = ['label', 'style', 'tone', 'actionKind', 'config', 'id', 'actionUrl', 'actionMethod',
      'actionBody', 'actionConfirm', 'actionRefresh'];
    const button: Record<string, unknown> = {};
    for (const key of keys) {
      if (actionCase[key] !== undefined) button[key] = actionCase[key];
      delete (actionCase as Record<string, unknown>)[key];
    }
    return button as TableActionCase;
  }

  isTmColumnEditable(column: TmColumnDef): boolean {
    return this.isTmEditable ? column.editable !== false : column.editable === true;
  }

  onTmColumnAlignChange(column: TmColumnDef, align: string | null): void {
    column.align = align === 'start' || align === 'center' || align === 'end' ? align : '';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  // ── Generic cell configuration (shared editor → table-cell-config.ts) ────────
  onTmColumnCellConfigChange(column: TmColumnDef, config: TableCellConfig | undefined): void {
    column.cellConfig = config;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }
  /** Convert currency: the column follows the Currency exchange rate (display only). */
  setTmColumnCurrency(column: TmColumnDef, on: boolean): void {
    this.onTmColumnCellConfigChange(column, normalizeTableCellConfig({ ...(column.cellConfig ?? {}), convertCurrency: on || undefined }));
  }
  /** Whether the column's cells edit through a select — the shared editor
   *  then exposes the dropdown values block. */
  tmColumnListsOptions(column: TmColumnDef): boolean {
    if (column.isAction || !(this.isTmEditable || this.isTmNormal) || !this.isTmColumnEditable(column)) return false;
    const control = column.control || 'text';
    return control === 'dropdown' || control === 'multiselect' || control === 'radio' || control === 'lookup' || control === 'autocomplete';
  }
  /** Other columns a rule may test (memoized — ng-select [items] must be stable). */
  private tmRuleFieldsMemo: { key: string; items: string[] } | null = null;
  tmRuleFields(column: TmColumnDef): string[] {
    const list = this.isTmToggleColumn(column) ? this.tmToggleColumns : this.tmColumns;
    const items = list.filter((c) => c.field && !c.isAction).map((c) => c.field);
    const key = items.join('\u0000');
    if (this.tmRuleFieldsMemo?.key !== key) this.tmRuleFieldsMemo = { key, items };
    return this.tmRuleFieldsMemo.items;
  }

  onTmColumnFilterFieldChange(column: TmColumnDef, field: string | null): void {
    column.filterField = field || '';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFilterOperatorChange(column: TmColumnDef, operator: string | null): void {
    column.filterOperator = operator || 'eq';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFilterValueChange(column: TmColumnDef, value: string): void {
    column.filterValue = value;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  columnsForTmColumn(column: TmColumnDef): string[] {
    return this.host.columnsForRow(column.id, this.tmDatasetFor(column));
  }

  isTmColumnLoading(column: TmColumnDef): boolean {
    return this.host.isDatasetColumnsLoading(this.tmDatasetFor(column));
  }

  tmColumnFieldPlaceholder(column: TmColumnDef): string {
    if (!this.tmDatasetFor(column)) return 'Select a dataset first';
    return this.isTmColumnLoading(column) ? 'Loading columns...' : 'Select column';
  }

  private defaultColumnDisplayName(field: string): string {
    return String(field || '')
      .replace(/[_\-.]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private tmActionFieldKey(column: TmColumnDef): string {
    return `__action_${String(column.id || '').replace(/[^a-zA-Z0-9_]/g, '') || 'col'}`;
  }

  private tmShapeFieldColumns(): Array<{ dataset: string; field: string; displayName: string }> {
    const fields = this.isTmMatrix
      ? [this.tmRowField, this.tmColumnField, this.tmValueField]
      : this.isTmPivot
        ? [...this.tmRowFields, ...this.tmColumnFields2, ...(this.tmValueFields.length ? this.tmValueFields : [this.tmValueField])]
        : [];
    const seen = new Set<string>();
    return fields
      .map((field) => String(field || '').trim())
      .filter((field) => field && !seen.has(field) && (seen.add(field), true))
      .map((field) => ({
        dataset: this.host.drillDataset,
        field,
        displayName: this.defaultColumnDisplayName(field),
      }));
  }

  /** The field compute reads: the column, or "<column>.<path>" for a value inside a JSON column. */
  private tmColumnFieldWithPath(column: TmColumnDef): string {
    const path = String(column.jsonPath ?? '').trim().replace(/^\.+|\.+$/g, '');
    return path ? `${column.field}.${path}` : column.field;
  }

  onTmColumnJsonPathChange(column: TmColumnDef, path: string): void {
    column.jsonPath = path;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** The column's formula, trimmed; '' when it has none. */
  private tmColumnFormula(column: TmColumnDef): string {
    return String(column.formula ?? '').trim();
  }

  private tmCapabilityColumns(cols: ReturnType<TableMatrixStepComponent['tmColumnEntries']>): TableMatrixColumn[] {
    return cols.map((column) => ({
      // A formula column's value comes back under its display name.
      field: column.formula ? column.displayName : column.field,
      label: column.displayName,
      ...(column.type ? { type: column.type } : {}),
      ...(column.decimals !== undefined ? { decimals: column.decimals } : {}),
      ...(column.actionByValue ? { actionByValue: column.actionByValue } : {}),
      ...(column.isAction && column.hiddenButtons?.length ? { hiddenButtons: column.hiddenButtons } : {}),
      ...(column.hidden ? { hidden: true } : {}),
      ...(column.saveAllTrigger ? { saveAllTrigger: true } : {}),
      ...(column.mandatory ? { mandatory: true } : {}),
      ...(column.isAction
        ? {
            isAction: true as const,
            ...(column.buttonIcon ? { buttonIcon: column.buttonIcon } : {}),
            ...(column.buttonLabel ? { buttonLabel: column.buttonLabel } : {}),
            actionKind: (column.actionKind || 'emit') as TableMatrixColumn['actionKind'],
            ...(column.actionKind === 'buttons' && column.cellButtons?.length ? { cellButtons: column.cellButtons } : {}),
            ...(column.popupComponentId ? { popupComponentId: column.popupComponentId } : {}),
            ...(column.popupData ? { popupData: column.popupData } : {}),
            ...(column.actionUrl ? { actionUrl: column.actionUrl } : {}),
            ...(column.actionMethod ? { actionMethod: column.actionMethod as TableMatrixColumn['actionMethod'] } : {}),
            ...(column.actionBody ? { actionBody: column.actionBody } : {}),
            ...(column.actionRowsPath ? { actionRowsPath: column.actionRowsPath } : {}),
            ...(column.actionRowsSortBy ? { actionRowsSortBy: column.actionRowsSortBy } : {}),
            ...(column.actionRowsSortDir ? { actionRowsSortDir: column.actionRowsSortDir } : {}),
            ...(Number(column.actionMaxRows) > 0 ? { actionMaxRows: Number(column.actionMaxRows) } : {}),
            ...(column.actionRowsColumns ? { actionRowsColumns: column.actionRowsColumns } : {}),
            ...(column.actionTitle ? { actionTitle: column.actionTitle } : {}),
            ...(column.actionConfirm ? { actionConfirm: column.actionConfirm } : {}),
            ...(column.actionRefresh ? { actionRefresh: true } : {}),
          }
        : {}),
      ...(column.align ? { align: column.align } : {}),
      ...(column.editable === false ? { editable: false as const } : {}),
      ...(column.editable !== false && column.control
        ? { control: column.control as TableMatrixColumn['control'] }
        : {}),
      // Generic cell configuration — the renderer resolves the per-cell look
      // (and the dropdown values) from it.
      ...(column.cellConfig ? { cellConfig: column.cellConfig } : {}),
    }));
  }

  private tmColumnEntries(source?: TmColumnDef[]): Array<{ dataset: string; field: string; displayName: string; editable?: boolean; control?: string; hidden?: boolean; saveAllTrigger?: boolean; mandatory?: boolean; isAction?: boolean; buttonIcon?: string; buttonLabel?: string; actionKind?: string; cellButtons?: TableCellButton[]; popupComponentId?: string; popupData?: Record<string, unknown>; actionUrl?: string; actionMethod?: string; actionBody?: string; actionRowsPath?: string; actionRowsSortBy?: string; actionRowsSortDir?: 'asc' | 'desc'; actionMaxRows?: number; actionRowsColumns?: string; actionTitle?: string; actionConfirm?: string; actionRefresh?: boolean; align?: 'start' | 'center' | 'end'; formula?: string; type?: 'number' | 'currency' | 'percent'; decimals?: number; actionByValue?: TableActionByValue; hiddenButtons?: string[]; filterField?: string; filterOperator?: string; filterValue?: string; cellConfig?: TableCellConfig }> {
    const list = source ?? this.tmColumns;
    const fallbackDataset = source ? this.tmToggleDatasetEffective : this.host.drillDataset;
    return list
      .filter((column) => column.field || column.isAction || this.tmColumnFormula(column))
      .map((column) => ({
        dataset: column.isAction ? '' : (column.dataset || fallbackDataset),
        // A formula column reads no field: the compute API evaluates it over the row.
        field: column.isAction
          ? this.tmActionFieldKey(column)
          : (this.tmColumnFormula(column) ? '' : this.tmColumnFieldWithPath(column)),
        displayName: column.isAction
          ? (column.displayName || '')
          : (column.displayName || (this.tmColumnFormula(column) ? 'Formula' : this.defaultColumnDisplayName(column.field))),
        ...(!column.isAction && this.tmColumnFormula(column) ? { formula: this.tmColumnFormula(column) } : {}),
        ...(!column.isAction && column.type ? { type: column.type } : {}),
        ...(!column.isAction && column.decimals !== undefined ? { decimals: column.decimals } : {}),
        ...(column.isAction && column.actionByValue ? { actionByValue: column.actionByValue } : {}),
        ...(column.isAction && column.hiddenButtons?.length ? { hiddenButtons: column.hiddenButtons } : {}),
        ...(this.isTmEditable
          ? (column.editable === false
              ? { editable: false }
              : { editable: true, control: column.control || 'text' })
          : (column.editable === true
              ? { editable: true, control: column.control || 'text' }
              : {})),
        ...(column.hidden ? { hidden: true } : {}),
        ...(column.saveAllTrigger ? { saveAllTrigger: true } : {}),
        ...(column.mandatory ? { mandatory: true } : {}),
        ...(column.isAction
          ? {
              isAction: true,
              ...(column.buttonIcon?.trim() ? { buttonIcon: column.buttonIcon.trim() } : {}),
              ...(column.buttonLabel?.trim() ? { buttonLabel: column.buttonLabel.trim() } : {}),
              actionKind: column.actionKind || 'emit',
              ...(column.actionKind === 'buttons' && column.cellButtons?.length ? { cellButtons: column.cellButtons } : {}),
              ...(column.actionKind === 'popupComponent' && column.popupComponentId?.trim()
                ? {
                    popupComponentId: column.popupComponentId.trim(),
                    ...(this.tmPopupDataFor(column.popupComponentId) ? { popupData: this.tmPopupDataFor(column.popupComponentId)! } : {}),
                  }
                : {}),
              ...(column.actionUrl?.trim() ? { actionUrl: column.actionUrl.trim() } : {}),
              ...(column.actionMethod ? { actionMethod: column.actionMethod } : {}),
              ...(column.actionBody?.trim() ? { actionBody: column.actionBody.trim() } : {}),
              ...(column.actionRowsPath?.trim() ? { actionRowsPath: column.actionRowsPath.trim() } : {}),
              ...(column.actionRowsSortBy?.trim() ? { actionRowsSortBy: column.actionRowsSortBy.trim() } : {}),
              ...(column.actionRowsSortDir ? { actionRowsSortDir: column.actionRowsSortDir } : {}),
              ...(Number(column.actionMaxRows) > 0 ? { actionMaxRows: Number(column.actionMaxRows) } : {}),
              ...(column.actionRowsColumns?.trim() ? { actionRowsColumns: column.actionRowsColumns.trim() } : {}),
              ...(column.actionTitle?.trim() ? { actionTitle: column.actionTitle.trim() } : {}),
              ...(column.actionConfirm?.trim() ? { actionConfirm: column.actionConfirm.trim() } : {}),
              ...(column.actionRefresh ? { actionRefresh: true } : {}),
            }
          : {}),
        ...(column.align ? { align: column.align } : {}),
        ...(column.filterValue?.trim()
          ? { filterField: column.filterField || column.field, filterOperator: column.filterOperator || 'eq', filterValue: column.filterValue.trim() }
          : {}),
        ...(!column.isAction && column.cellConfig ? { cellConfig: column.cellConfig } : {}),
      }));
  }

  get tmToggleDatasetEffective(): string {
    return this.tmToggleDataset || this.host.drillDataset;
  }
  onTmToggleDatasetChange(dataset: string | null): void {
    this.tmToggleDataset = dataset || '';
    this.tmToggleColumns = [];
    if (this.tmToggleDataset) this.host.loadColumnsForDataset(this.tmToggleDataset);
    this.onTableMatrixTypeChange();
  }
  private buildTmToggleView(): Record<string, unknown> | undefined {
    if (!this.tmToggleEnabled || this.isTmDrilldown) return undefined;
    const computeUrl = resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.generateConfigApi, '/components/compute');
    const dataset = this.tmToggleDatasetEffective;
    const columns = this.tmColumnEntries(this.tmToggleColumns);
    // Only Design Builder provides the object request hook. Layout Builder
    // retains its normal compute definitions for these views.
    const custom = this.host.requestGenerate && this.host.buildApiCallConfig.enabled
      ? { customApi: sanitizeApiCallConfig(this.host.buildApiCallConfig) } : {};
    return {
      enabled: true,
      labels: [this.tmToggleLabel1 || 'View 1', this.tmToggleLabel2 || 'View 2'],
      default: 1,
      view1: {
        apiUrl: computeUrl,
        requestPayload: { ...this.buildFlatTableComputePayload(), ...custom },
      },
      view2: {
        capabilities: {
          ...(columns.length ? { columns: this.tmCapabilityColumns(columns) } : {}),
          cellEditing: this.isTmNormal && columns.some((c) => c.editable === true && c.control),
          dataset,
        },
        apiUrl: computeUrl,
        requestPayload: {
          ...custom,
          componentId: this.host.componentId,
          componentType: 'TABLE',
          tableType: 'NORMAL',
          subType: this.tmType,
          dashboardId: this.host.dashboardId,
          title: this.host.title.trim(),
          dataset,
          columns,
          aggregation: this.tmAggregation,
        },
      },
    };
  }
  private restoreTmToggleView(raw: unknown): void {
    const block = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    this.tmToggleEnabled = block['enabled'] === true;
    const labels = Array.isArray(block['labels']) ? block['labels'] as unknown[] : [];
    this.tmToggleLabel1 = String(labels[0] ?? '');
    this.tmToggleLabel2 = String(labels[1] ?? '');
    const view2 = (block['view2'] && typeof block['view2'] === 'object' ? block['view2'] : {}) as Record<string, unknown>;
    const request = (view2['requestPayload'] && typeof view2['requestPayload'] === 'object' ? view2['requestPayload'] : {}) as Record<string, unknown>;
    this.tmToggleDataset = String(request['dataset'] ?? '');
    this.tmToggleColumns = this.tmColumnDefsFrom(request['columns']);
    new Set(this.tmToggleColumns.map((column) => column.dataset || this.tmToggleDatasetEffective).filter(Boolean))
      .forEach((dataset) => this.host.loadColumnsForDataset(dataset));
  }

  private restoreTmColumns(raw: unknown, legacyInitialFilters?: unknown): void {
    this.tmColumns = this.tmColumnDefsFrom(raw, legacyInitialFilters);
    const datasets = new Set(this.tmColumns.map((column) => column.dataset || this.host.drillDataset).filter(Boolean));
    datasets.forEach((dataset) => this.host.loadColumnsForDataset(dataset));
  }

  private tmColumnDefsFrom(raw: unknown, legacyInitialFilters?: unknown): TmColumnDef[] {
    const list = Array.isArray(raw) ? raw : [];
    const legacyByField = new Map<string, { operator: string; value: string }>();
    if (Array.isArray(legacyInitialFilters)) {
      legacyInitialFilters.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const record = entry as Record<string, unknown>;
        const field = String(record['filterKey'] ?? '');
        if (!field) return;
        legacyByField.set(field, {
          operator: String(record['filterOperator'] ?? 'eq'),
          value: String(record['filterValue'] ?? ''),
        });
      });
    }
    return list
      .map((entry): TmColumnDef => {
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>;
          // "approval_history.status" = the "status" inside the approval_history column.
          const [field, ...jsonPath] = String(record['field'] ?? '').split('.');
          const legacy = legacyByField.get(field);
          return {
            id: this.nextTmColumnId(),
            dataset: String(record['dataset'] ?? ''),
            field,
            jsonPath: jsonPath.join('.'),
            displayName: String(record['displayName'] ?? record['label'] ?? ''),
            editable: typeof record['editable'] === 'boolean' ? record['editable'] : undefined,
            control: typeof record['control'] === 'string' ? record['control'] : '',
            hidden: record['hidden'] === true,
            saveAllTrigger: record['saveAllTrigger'] === true,
            mandatory: record['mandatory'] === true,
            isAction: record['isAction'] === true,
            buttonIcon: String(record['buttonIcon'] ?? ''),
            buttonLabel: String(record['buttonLabel'] ?? ''),
            actionKind: (record['actionKind'] as TmColumnDef['actionKind']) ?? undefined,
            cellButtons: normalizeTableCellButtons(record['cellButtons']),
            actionUrl: String(record['actionUrl'] ?? ''),
            actionMethod: (record['actionMethod'] as TmColumnDef['actionMethod']) ?? undefined,
            actionBody: String(record['actionBody'] ?? ''),
            popupComponentId: String(record['popupComponentId'] ?? ''),
            actionRowsPath: String(record['actionRowsPath'] ?? ''),
            actionRowsSortBy: String(record['actionRowsSortBy'] ?? ''),
            actionRowsSortDir: (record['actionRowsSortDir'] as TmColumnDef['actionRowsSortDir']) ?? undefined,
            actionMaxRows: Number(record['actionMaxRows']) > 0 ? Number(record['actionMaxRows']) : undefined,
            actionRowsColumns: String(record['actionRowsColumns'] ?? ''),
            actionTitle: String(record['actionTitle'] ?? ''),
            actionConfirm: String(record['actionConfirm'] ?? ''),
            actionRefresh: record['actionRefresh'] === true,
            align: this.asTmColumnAlign(record['align']),
            formula: String(record['formula'] ?? ''),
            type: this.asTmColumnType(record['type']),
            decimals: this.asTmColumnDecimals(record['decimals']),
            // A copy: the per-row button editor edits it in place, and the saved config must
            // only change on Update Configuration.
            actionByValue: record['actionByValue'] && typeof record['actionByValue'] === 'object'
              ? structuredClone(record['actionByValue']) as TableActionByValue
              : undefined,
            hiddenButtons: Array.isArray(record['hiddenButtons'])
              ? (record['hiddenButtons'] as unknown[]).map((label) => String(label ?? '').trim()).filter(Boolean)
              : undefined,
            filterField: String(record['filterField'] ?? ''),
            filterOperator: String(record['filterOperator'] ?? legacy?.operator ?? 'eq'),
            filterValue: String(record['filterValue'] ?? legacy?.value ?? ''),
            cellConfig: normalizeTableCellConfig(record['cellConfig']),
          };
        }
        const field = String(entry ?? '');
        const legacy = legacyByField.get(field);
        return {
          id: this.nextTmColumnId(), dataset: '', field, displayName: '', align: '', filterField: '',
          filterOperator: legacy?.operator ?? 'eq', filterValue: legacy?.value ?? '',
        };
      })
      .filter((column) => column.field || column.isAction || column.formula?.trim());
  }

  private asTmColumnAlign(value: unknown): '' | 'start' | 'center' | 'end' {
    return value === 'start' || value === 'center' || value === 'end' ? value : '';
  }

  private asTmColumnType(value: unknown): '' | 'number' | 'currency' | 'percent' {
    return value === 'number' || value === 'currency' || value === 'percent' ? value : '';
  }

  private asTmColumnDecimals(value: unknown): number | undefined {
    const decimals = value === '' || value === null || value === undefined ? NaN : Number(value);
    return Number.isInteger(decimals) && decimals >= 0 && decimals <= 6 ? decimals : undefined;
  }

  private tmColumnIdCounter = 0;

  private nextTmColumnId(): string {
    this.tmColumnIdCounter += 1;
    return `tm-column-${this.tmColumnIdCounter}`;
  }

  buildWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const capabilities = this.buildTableMatrixCapabilities(format);
    const widgetId = 'dvm-tile-table-matrix-' + tileId;
    if (this.isTmDrilldown) {
      const drilldownData = this.drill.buildDrillDownWidgetData(response, format);
      drilldownData['widgetId'] = widgetId + '-drill';
      return {
        widgetId,
        title: this.host.title.trim(),
        type: 'table-matrix',
        capabilities,
        drilldownData,
      };
    }
    const rows = extractTableMatrixRows(response);
    const toggleView = this.buildTmToggleView();
    return {
      widgetId,
      title: this.host.title.trim(),
      type: 'table-matrix',
      capabilities,
      staticData: rows,
      ...(toggleView ? { toggleView } : {}),
    };
  }

  buildTableMatrixCapabilities(format?: Record<string, unknown>): TableMatrixCapabilities {
    const fmt = format || {};
    const options = (this.host.layout.options as Record<string, unknown> | undefined) || {};
    const chartDefaults = (options['tableMatrixChartDefaults'] as Record<string, unknown>) || {};
    const typeFlags = this.tmFlagsFor(this.tmType);

    const caps: TableMatrixCapabilities = {
      tableType: this.tmType,
      typeFlags,
      chartDefaults: chartDefaults as TableMatrixCapabilities['chartDefaults'],
      showValues: fmt['showValues'] !== false,
      numberFormat: fmt['numberFormat'] as string | undefined,
      decimals: fmt['decimals'] != null ? Number(fmt['decimals']) : undefined,
      dateTimeFormat: fmt['dateTimeFormat'] === true,
      dateTimeParts: fmt['dateTimeParts'] as TableMatrixCapabilities['dateTimeParts'],
      density: (fmt['density'] as string) || 'comfortable',
      striped: fmt['striped'] !== false,
      showColumnLines: fmt['showColumnLines'] !== false,
      headerBackground: fmt['headerBackground'] as string | undefined,
      headerColor: fmt['headerColor'] as string | undefined,
      headerFontSize: Number(fmt['headerFontSize']) > 0 ? Number(fmt['headerFontSize']) : undefined,
      stickyHeader: fmt['stickyHeader'] !== false,
      stickyFooter: fmt['stickyFooter'] !== false,
      freezeFirstColumn: fmt['freezeFirstColumn'] === true,
      freezeColumnCount: fmt['freezeColumnCount'] != null ? Number(fmt['freezeColumnCount']) : undefined,
      freezeRowCount: fmt['freezeRowCount'] != null ? Number(fmt['freezeRowCount']) : undefined,
      enableColumnReorder: fmt['enableColumnReorder'] === true,
      enableColumnResize: fmt['enableColumnResize'] === true,
      virtualScroll: fmt['virtualScroll'] === true,
      pageSize: fmt['pageSize'] != null ? Number(fmt['pageSize']) : undefined,
      enableSearch: fmt['enableSearch'] !== false,
      enableColumnSearch: fmt['enableColumnSearch'] === true,
      enableAdvancedFilter: fmt['enableAdvancedFilter'] === true,
      enableValueFilter: fmt['enableValueFilter'] === true,
      fixedRowFilter: typeof fmt['fixedRowFilter'] === 'string' ? fmt['fixedRowFilter'] : undefined,
      rowFilterPreset: typeof fmt['rowFilterPreset'] === 'string' && fmt['rowFilterPreset'] ? fmt['rowFilterPreset'] : undefined,
      enableRefresh: fmt['enableRefresh'] === true,
      enableSavePrefs: fmt['enableSavePrefs'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      rowSelection: (fmt['rowSelection'] as string) || 'single',
      crossFilter: fmt['crossFilter'] === true,
      enableExport: fmt['enableExport'] !== false,
      defaultSort: (fmt['defaultSort'] as string) || undefined,
      defaultSortDir: fmt['defaultSortDir'] === 'desc' ? 'desc' : undefined,
      yearFilterCount: yearFilterCountFrom(fmt),
      yearFilterBase: yearFilterBaseFrom(fmt),
      yearFilterField: yearFilterFieldFrom(fmt),
      showSubtotals: fmt['showSubtotals'] === true,
      showGrandTotal: fmt['showGrandTotal'] === true,
      editMode: (fmt['editMode'] as 'cell' | 'row') || 'cell',
      enableRowAdd: this.isTmEditable && fmt['enableRowAdd'] !== false,
      enableRowInsert: this.isTmEditable && fmt['enableRowInsert'] !== false,
      enableRowDelete: this.isTmEditable && fmt['enableRowDelete'] !== false,
      enableSoftDelete: this.isTmEditable && fmt['enableSoftDelete'] === true,
      enableRowDuplicate: this.isTmEditable && fmt['enableRowDuplicate'] !== false,
      enableCopyPaste: this.isTmEditable && fmt['enableCopyPaste'] === true,
      enableUndoRedo: fmt['enableUndoRedo'] !== false,
      showRowState: fmt['showRowState'] !== false,
      enableValidation: fmt['enableValidation'] !== false,
      enableRowSave: fmt['enableRowSave'] === true,
      enableBulkEdit: fmt['enableBulkEdit'] === true,
      enableAutoSave: fmt['enableAutoSave'] === true,
      autoSaveDebounceMs: fmt['autoSaveDebounceMs'] != null ? Number(fmt['autoSaveDebounceMs']) : undefined,
      saveOnRowExit: fmt['saveOnRowExit'] === true,
      saveUrl: (fmt['saveUrl'] as string) || undefined,
      bulkSaveUrl: (fmt['bulkSaveUrl'] as string) || undefined,
      createUrl: (fmt['createUrl'] as string) || undefined,
      updateUrl: (fmt['updateUrl'] as string) || undefined,
      deleteUrl: (fmt['deleteUrl'] as string) || undefined,
      enableOptimisticLock: fmt['enableOptimisticLock'] === true,
      versionField: (fmt['versionField'] as string) || undefined,
      transactional: fmt['transactional'] === true,
      payloadTemplates: (fmt['payloadTemplates'] as TableMatrixCapabilities['payloadTemplates']) || undefined,
      enableWorkflow: fmt['enableWorkflow'] === true,
      workflowUrl: (fmt['workflowUrl'] as string) || undefined,
      enableComments: fmt['enableComments'] === true,
      enableAttachments: fmt['enableAttachments'] === true,
      enableAuditTrail: fmt['enableAuditTrail'] === true,
      enableUpload: fmt['enableUpload'] === true,
      uploadButtonLabel: (fmt['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (fmt['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (fmt['uploadUrl'] as string) || undefined,
      componentId: this.host.componentId,
      dataset: this.host.drillDataset || undefined,
      computeUrl: resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.generateConfigApi, '/components/compute'),
    };

    if (this.tmNavEnabled) {
      caps.navigation = {
        enabled: true,
        filterKey: this.tmNavFilterKey || undefined,
        navigableColumns: this.tmNavColumns.length ? [...this.tmNavColumns] : undefined,
        targetDashboard: this.tmNavTargetDashboard || undefined,
        targetComponents: this.tmNavTargetComponents.length ? [...this.tmNavTargetComponents] : [],
        buttonLabel: this.tmNavButtonLabel || 'Navigate',
      };
    }

    if (!this.isTmDrilldown) {
      const cols = this.tmColumnEntries();
      if (cols.length) {
        caps.columns = this.tmCapabilityColumns(cols);
      }
      if (this.isTmNormal && cols.some((column) => column.editable === true && column.control)) {
        caps.cellEditing = true;
      }
      if (this.isTmNormal) {
        if (this.tmGroupBy) caps.groupBy = this.tmGroupBy;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'none';
      }
      if (this.isTmMatrix) {
        caps.rowField = this.tmRowField || undefined;
        caps.columnField = this.tmColumnField || undefined;
        caps.valueField = this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
      }
      if (this.isTmPivot) {
        caps.rowFields = this.tmRowFields.length ? [...this.tmRowFields] : undefined;
        caps.columnFields = this.tmColumnFields2.length ? [...this.tmColumnFields2] : undefined;
        caps.valueFields = this.tmValueFields.length ? [...this.tmValueFields] : undefined;
        caps.valueField = this.tmValueFields[0] || this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
      }
      if (this.isTmGrouped) {
        caps.groupByFields = this.tmGroupByFields.length ? [...this.tmGroupByFields] : undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmTreegrid) {
        caps.treeIdField = this.tmTreeIdField || undefined;
        caps.treeParentField = this.tmTreeParentField || undefined;
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmFinancial) {
        caps.roleField = this.tmRoleField || undefined;
        caps.indentField = this.tmIndentField || undefined;
      }
    } else {
      this.drill.applyToTableMatrixCapabilities(caps, fmt);
    }
    return caps;
  }

  applyFormatToCapabilities(
    caps: Record<string, unknown>,
    format: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...caps,
      showValues: format['showValues'] !== false,
      numberFormat: format['numberFormat'],
      decimals: format['decimals'] != null ? Number(format['decimals']) : caps['decimals'],
      dateTimeFormat: format['dateTimeFormat'] === true,
      dateTimeParts: format['dateTimeParts'] as TableMatrixCapabilities['dateTimeParts'],
      density: (format['density'] as string) || 'comfortable',
      striped: format['striped'] !== false,
      showColumnLines: format['showColumnLines'] !== false,
      headerBackground: format['headerBackground'],
      headerColor: format['headerColor'],
      headerFontSize: Number(format['headerFontSize']) > 0 ? Number(format['headerFontSize']) : undefined,
      fontFamily: (format['fontFamily'] as string) || undefined,
      fontSize: Number(format['fontSize']) > 0 ? Number(format['fontSize']) : caps['fontSize'],
      stickyHeader: format['stickyHeader'] !== false,
      stickyFooter: format['stickyFooter'] !== false,
      freezeFirstColumn: format['freezeFirstColumn'] === true,
      freezeColumnCount: format['freezeColumnCount'] != null ? Number(format['freezeColumnCount']) : caps['freezeColumnCount'],
      freezeRowCount: format['freezeRowCount'] != null ? Number(format['freezeRowCount']) : caps['freezeRowCount'],
      enableColumnReorder: format['enableColumnReorder'] === true,
      enableColumnResize: format['enableColumnResize'] === true,
      virtualScroll: format['virtualScroll'] === true,
      pageSize: format['pageSize'] != null ? Number(format['pageSize']) : caps['pageSize'],
      enableSearch: format['enableSearch'] !== false,
      enableColumnSearch: format['enableColumnSearch'] === true,
      enableAdvancedFilter: format['enableAdvancedFilter'] === true,
      enableValueFilter: format['enableValueFilter'] === true,
      fixedRowFilter: typeof format['fixedRowFilter'] === 'string' ? format['fixedRowFilter'] : undefined,
      rowFilterPreset: typeof format['rowFilterPreset'] === 'string' && format['rowFilterPreset'] ? format['rowFilterPreset'] : undefined,
      enableRefresh: format['enableRefresh'] === true,
      enableSavePrefs: format['enableSavePrefs'] === true,
      enableExcelExport: format['enableExcelExport'] === true,
      enablePdfExport: format['enablePdfExport'] === true,
      enablePrint: format['enablePrint'] === true,
      rowSelection: (format['rowSelection'] as string) || 'single',
      crossFilter: format['crossFilter'] === true,
      enableExport: format['enableExport'] !== false,
      defaultSort: (format['defaultSort'] as string) || undefined,
      defaultSortDir: format['defaultSortDir'] === 'desc' ? 'desc' : undefined,
      yearFilterCount: yearFilterCountFrom(format),
      yearFilterBase: yearFilterBaseFrom(format),
      yearFilterField: yearFilterFieldFrom(format),
      showSubtotals: format['showSubtotals'] === true,
      showGrandTotal: format['showGrandTotal'] === true,
      groupsExpanded: format['groupsExpanded'] !== false,
      rawLeafRows: format['drillRawLeafRows'] === true,
      headerGroupBy: this.drill.drillHeaderGroupByFrom(format),
      ...(caps['rowTotal'] === true && String(format['drillRowTotalLabel'] ?? '').trim()
        ? { rowTotalLabel: String(format['drillRowTotalLabel']).trim() }
        : {}),
      enableUpload: format['enableUpload'] === true,
      uploadButtonLabel: (format['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (format['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (format['uploadUrl'] as string) || caps['uploadUrl'],
      saveUrl: (format['saveUrl'] as string) || caps['saveUrl'],
      bulkSaveUrl: (format['bulkSaveUrl'] as string) || caps['bulkSaveUrl'],
      createUrl: (format['createUrl'] as string) || caps['createUrl'],
      updateUrl: (format['updateUrl'] as string) || caps['updateUrl'],
      deleteUrl: (format['deleteUrl'] as string) || caps['deleteUrl'],
      versionField: (format['versionField'] as string) || caps['versionField'],
      enableOptimisticLock: format['enableOptimisticLock'] != null ? format['enableOptimisticLock'] === true : caps['enableOptimisticLock'],
      transactional: format['transactional'] != null ? format['transactional'] === true : caps['transactional'],
      payloadTemplates: format['payloadTemplates'] ?? caps['payloadTemplates'],
    };
  }

  private buildTableMatrixComputePayload(): Record<string, unknown> {
    if (!this.isTmDrilldown) {
      return this.buildFlatTableComputePayload();
    }
    return this.buildTreeTableComputePayload();
  }

  private buildFlatTableComputePayload(): Record<string, unknown> {
    const picked = this.tmColumnEntries();
    const columns = picked.length ? picked : this.tmShapeFieldColumns();
    return {
      componentId: this.host.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: this.tmType,
      dashboardId: this.host.dashboardId,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      columns,
      ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
      aggregation: this.tmAggregation,
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  private buildTreeTableComputePayload(): Record<string, unknown> {
    const selected = this.tmColumnFields.filter(Boolean);
    const columns = selected.length ? selected : [...(this.host.cachedColumns(this.host.drillDataset) || [])];
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const isNumeric = (field: string): boolean => {
      const sample = rows.find((row) => row[field] != null && row[field] !== '');
      return typeof sample?.[field] === 'number';
    };
    const aggregationMap: Record<string, string> = { sum: 'SUM', avg: 'AVG', count: 'COUNT' };
    const aggregation = aggregationMap[this.tmAggregation] || 'SUM';

    const numericFields = columns.filter(isNumeric);
    let dimensionFields = this.tmGroupBy && this.tmAggregation !== 'none'
      ? [this.tmGroupBy]
      : columns.filter((field) => !numericFields.includes(field));
    let measureFields = numericFields.filter((field) => !dimensionFields.includes(field));
    if (!dimensionFields.length && measureFields.length) {
      dimensionFields = [measureFields[0]];
      measureFields = measureFields.slice(1);
    }
    const measures = measureFields.length
      ? measureFields.map((field) => ({ field, label: field, aggregation, rollUpStrategy: 'SUM', total: false }))
      : [{ field: dimensionFields[dimensionFields.length - 1], label: dimensionFields[dimensionFields.length - 1], aggregation: 'COUNT', rollUpStrategy: 'SUM', total: false }];

    return {
      componentId: this.host.componentId,
      componentType: 'TABLE',
      tableType: 'TABLE_MATRIX',
      subType: this.tmType,
      dashboardId: this.host.dashboardId,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      dimensions: dimensionFields.map((field, index) => ({ level: index + 1, field, label: field })),
      measures,
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  reshapeEditingDrillTile(): void {
    if (!this.host.editingTileId || !this.drill.isDrillClientMode) return;
    const rows = this.drill.drillRowsCache.get(this.host.editingTileId);
    if (!rows) return;
    const tile = this.host.findTileAnywhere(this.host.editingTileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const norm = (s: string): string => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const keys = new Set<string>();
    rows.slice(0, 200).forEach((row) => Object.keys(row || {}).forEach((k) => keys.add(norm(k))));
    const present = (row: { field: string; label?: string }): boolean =>
      !row.field || keys.has(norm(row.field)) || (!!row.label && keys.has(norm(row.label)));
    const all = [...this.drill.drillDownDimensions, ...this.drill.drillColumnDimensions, ...this.drill.drillDownMeasures];
    if (!all.every(present)) return;
    tile.tableMatrixData = this.buildWidgetData(this.drill.buildDrillClientTreeResponse(rows), tile.id, tile.format);
    this.host.updateTileInPlace({ ...tile });
  }

  onFormatChanged(): void {
    if (!this.host.editingTileId) return;
    const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
    const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'TABLE_MATRIX') return;
    tile.format = { ...(tile.format || {}), ...this.host.barFormat };
    this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? tile : t));
    this.refreshEditingTableMatrixTile();
  }

  /** The table tile's columns were re-ordered on the canvas: mirror the order in the form. */
  syncColumnOrderFromTile(order: string[]): void {
    if (!this.tmColumns.length) return;
    const byField = new Map(this.tmColumns.map((column) => [column.field, column]));
    const reordered = order.map((field) => byField.get(field)).filter((c): c is TmColumnDef => !!c);
    const mentioned = new Set(reordered.map((column) => column.id));
    this.tmColumns = [...reordered, ...this.tmColumns.filter((column) => !mentioned.has(column.id))];
  }

  /** Drop the column fields the new primary dataset does not have (deferred until its columns are known). */
  private pruneColumnsForDataset(dataset: string): void {
    if (!dataset || !this.tmColumns.length) return;
    const columns = this.host.cachedColumns(dataset);
    if (!columns) {
      this.pendingPrune = dataset;
      return;
    }
    this.pendingPrune = '';
    const known = new Set(columns);
    let tmChanged = false;
    this.tmColumns.forEach((column) => {
      if (!column.dataset && column.field && !known.has(column.field)) {
        column.field = '';
        tmChanged = true;
      }
    });
    if (tmChanged) this.tmColumns = [...this.tmColumns];
    if (!this.tmToggleDataset) {
      let toggleChanged = false;
      this.tmToggleColumns.forEach((column) => {
        if (!column.dataset && column.field && !known.has(column.field)) {
          column.field = '';
          toggleChanged = true;
        }
      });
      if (toggleChanged) { this.tmToggleColumns = [...this.tmToggleColumns]; tmChanged = true; }
    }
    if (tmChanged) {
      this.host.invalidatePreview();
      this.ui.showError(`Some fields pointed at columns "${dataset}" doesn't have — re-pick them for the new dataset.`);
    }
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    return this.isTmDrilldown ? this.drill.blockers() : [];
  }

  preGenerateError(): string {
    return this.isTmDrilldown ? this.drill.staleFieldsError() : '';
  }

  buildPayload(): Record<string, unknown> {
    const base = {
      componentId: this.host.componentId,
      componentType: 'TABLE',
      tableType: this.isTmDrilldown ? 'TABLE_MATRIX' : 'NORMAL',
      subType: this.tmType,
      dashboardId: this.host.dashboardId,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
    if (this.isTmDrilldown) {
      return { ...base, ...this.drill.tableMatrixPayloadFields() };
    }
    const columns = this.tmColumnEntries().length ? this.tmColumnEntries() : this.tmShapeFieldColumns();
    const toggleView = this.buildTmToggleView();
    return {
      ...base,
      columns,
      ...(toggleView ? { toggleView } : {}),
      ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
      aggregation: this.tmAggregation,
      ...(this.tmNavEnabled
        ? {
            navigation: {
              enabled: true,
              filterKey: this.tmNavFilterKey,
              navigableColumns: this.tmNavColumns,
              targetDashboard: this.tmNavTargetDashboard,
              targetComponents: this.tmNavTargetComponents,
              buttonLabel: this.tmNavButtonLabel,
            },
          }
        : {}),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const caps = ((tile.tableMatrixData as Record<string, unknown>)?.['capabilities'] as Record<string, unknown>) || {};
    this.tmType = String(payload['subType'] ?? caps['tableType'] ?? 'normal');
    const migratedPivot = this.legacyPivotToDrilldown(payload, caps);
    this.tmMigrationNotice = '';
    if (migratedPivot) {
      this.tmType = 'drilldown';
    }
    this.host.barFormat = { ...(tile.format || {}) };
    if (this.isTmDrilldown && this.host.barFormat['title'] == null) this.host.barFormat['title'] = this.host.title;
    if (migratedPivot || this.isTmDrilldown) {
      this.drill.loadFromTableMatrix(tile, payload, caps, migratedPivot);
      return;
    }
    this.restoreTmColumns(payload['columns'], payload['initialFilters'] ?? caps['initialFilters']);
    this.restoreTmToggleView(payload['toggleView'] ?? caps['toggleView']);
    this.tmGroupBy = String(payload['groupBy'] ?? caps['groupBy'] ?? '');
    this.tmAggregation = String(payload['aggregation'] ?? caps['aggregation'] ?? 'none');
    this.tmRowField = String(caps['rowField'] ?? '');
    this.tmColumnField = String(caps['columnField'] ?? '');
    this.tmValueField = String(caps['valueField'] ?? '');
    this.tmValueFields = Array.isArray(caps['valueFields']) && (caps['valueFields'] as unknown[]).length
      ? (caps['valueFields'] as unknown[]).map(String)
      : (this.tmValueField ? [this.tmValueField] : []);
    this.tmRowFields = Array.isArray(caps['rowFields']) ? (caps['rowFields'] as unknown[]).map(String) : [];
    this.tmColumnFields2 = Array.isArray(caps['columnFields']) ? (caps['columnFields'] as unknown[]).map(String) : [];
    this.tmGroupByFields = Array.isArray(caps['groupByFields']) ? (caps['groupByFields'] as unknown[]).map(String) : [];
    this.tmGroupsExpanded = caps['groupsExpanded'] !== false;
    this.tmTreeIdField = String(caps['treeIdField'] ?? '');
    this.tmTreeParentField = String(caps['treeParentField'] ?? '');
    this.tmRoleField = String(caps['roleField'] ?? '');
    this.tmIndentField = String(caps['indentField'] ?? '');
    const nav = ((payload['navigation'] as Record<string, unknown>)
      || (caps['navigation'] as Record<string, unknown>)
      || {}) as Record<string, unknown>;
    this.tmNavEnabled = nav['enabled'] === true;
    this.tmNavFilterKey = String(nav['filterKey'] ?? '');
    this.tmNavColumns = Array.isArray(nav['navigableColumns']) ? (nav['navigableColumns'] as unknown[]).map(String) : [];
    this.tmNavTargetDashboard = String(nav['targetDashboard'] ?? '');
    this.tmNavTargetComponents = Array.isArray(nav['targetComponents']) ? (nav['targetComponents'] as unknown[]).map(String) : [];
    this.tmNavButtonLabel = String(nav['buttonLabel'] ?? 'Navigate');
    this.tmNavAccordionOpen = this.tmNavEnabled;
    if (this.tmNavTargetDashboard) this.host.ensureNavTargetInList(this.tmNavTargetDashboard);
  }

  reset(): void {
    this.tmType = 'normal';
    this.tmMigrationNotice = '';
    this.tmColumns = [];
    this.tmToggleEnabled = false;
    this.tmToggleLabel1 = '';
    this.tmToggleLabel2 = '';
    this.tmToggleDataset = '';
    this.tmToggleColumns = [];
    this.tmGroupBy = '';
    this.tmAggregation = 'none';
    this.tmRowField = '';
    this.tmColumnField = '';
    this.tmValueField = '';
    this.tmValueFields = [];
    this.tmRowFields = [];
    this.tmColumnFields2 = [];
    this.tmGroupByFields = [];
    this.tmGroupsExpanded = true;
    this.tmTreeIdField = '';
    this.tmTreeParentField = '';
    this.tmRoleField = '';
    this.tmIndentField = '';
    this.tmNavEnabled = false;
    this.tmNavFilterKey = '';
    this.tmNavColumns = [];
    this.tmNavTargetDashboard = '';
    this.tmNavTargetComponents = [];
    this.tmNavButtonLabel = 'Navigate';
    this.tmNavAccordionOpen = false;
    this.drill.reset();
  }

  onDatasetChanged(dataset: string): void {
    this.pruneColumnsForDataset(dataset);
    if (this.isTmDrilldown) this.drill.onDatasetChanged(dataset);
  }

  onDatasetColumnsLoaded(dataset: string): void {
    if (this.pendingPrune === dataset) this.pruneColumnsForDataset(dataset);
    if (this.isTmDrilldown) this.drill.onDatasetColumnsLoaded(dataset);
  }

  /**
   * Drill-down (client mode) posts the flat compute payload and builds the
   * tree client-side; flat tables post their compute payload. The server-tree
   * drill-down keeps the shell's default flow (POST payload → tile).
   */
  generate(payload: Record<string, unknown>, url: string): boolean {
    if (this.drill.isDrillClientMode) {
      (this.host.requestGenerate?.(url, this.drill.buildDrillClientComputePayload()) ?? this.http.post<Record<string, unknown>>(url, this.drill.buildDrillClientComputePayload())).subscribe({
        next: (response) => {
          this.host.isGenerating = false;
          const rows = extractTableMatrixRows(response);
          const updated = this.host.addTileFromResponse(this.drill.buildDrillClientTreeResponse(rows), payload);
          if (!this.host.editingTileId) {
            const last = this.host.dashboardTiles[this.host.dashboardTiles.length - 1];
            if (last?.layoutType === 'TABLE_MATRIX') this.host.editingTileId = last.id;
          }
          if (this.host.editingTileId) this.drill.drillRowsCache.set(this.host.editingTileId, rows);
          this.ui.showSuccess(updated ? 'Drill-down table updated on the dashboard.' : 'Drill-down table added to the dashboard.');
        },
        error: (error: HttpErrorResponse) => {
          this.host.isGenerating = false;
          this.host.generateError = error.error?.message || error.message || 'Unable to generate the drill-down table.';
          this.ui.showError(this.host.generateError);
        },
      });
      return true;
    }
    if (!this.isTmDrilldown) {
      (this.host.requestGenerate?.(url, this.buildTableMatrixComputePayload()) ?? this.http.post<Record<string, unknown>>(url, this.buildTableMatrixComputePayload())).subscribe({
        next: (response) => {
          this.host.isGenerating = false;
          const rows = extractTableMatrixRows(response);
          const updated = this.host.addTileFromResponse({ data: rows }, payload);
          if (!this.host.editingTileId) {
            const last = this.host.dashboardTiles[this.host.dashboardTiles.length - 1];
            if (last?.layoutType === 'TABLE_MATRIX') this.host.editingTileId = last.id;
          }
          this.ui.showSuccess(updated ? 'Table updated on the dashboard.' : 'Table added to the dashboard.');
        },
        error: (error: HttpErrorResponse) => {
          this.host.isGenerating = false;
          this.host.generateError = error.error?.message || error.message || 'Unable to generate the table.';
          this.ui.showError(this.host.generateError);
        },
      });
      return true;
    }
    return false;
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    return { ...base, tableMatrixData: this.buildWidgetData(response, base.id, base.format) };
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.tableMatrixData) return;
    const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
    tile.tableMatrixData = {
      ...tile.tableMatrixData,
      title: (format['title'] as string) || tile.title,
      capabilities: this.applyFormatToCapabilities(caps, format),
    };
  }

  /** Flat tables take the rows as `staticData`; drill-down tables rebuild the tree (client mode builds it from the rows). */
  applyRecomputeResult(tile: DashboardTile, { result, format }: TileRecomputeResult): boolean {
    const rows = extractTableMatrixRows(result);
    const existing = (tile.tableMatrixData as Record<string, unknown>) || {};
    const caps = existing['capabilities'] as Record<string, unknown> | undefined;
    const isDrill = !!caps && (caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true);
    if (isDrill) {
      const source = caps['drillDataMode'] === 'client'
        ? this.drill.buildDrillClientTreeResponse(rows, this.drill.drillClientConfigFromCaps(caps))
        : result;
      const drilldownData = this.drill.buildDrillDownWidgetData(source, format);
      drilldownData['widgetId'] = 'dvm-tile-table-matrix-' + tile.id + '-drill';
      tile.tableMatrixData = { ...existing, drilldownData };
    } else {
      tile.tableMatrixData = { ...existing, staticData: rows };
    }
    return true;
  }
}
